/**
 * ImageContentSection
 *
 * A full-width two-column section with a photo on one side and text content
 * on the other. Used on consulting service pages to highlight key value propositions.
 *
 * The image side fills its column edge-to-edge with object-cover.
 * The content side contains a heading, an optional description, and an optional
 * list of bullet items each with an icon, a heading, and supporting text.
 *
 * Props:
 * - imagePosition ("left" | "right", optional): Which side the image appears on. Defaults to "left".
 * - imageSrc (string, required): URL of the section image.
 * - imageAlt (string, required): Alt text for the section image.
 * - title (string, required): The heading text. An orange period is appended automatically.
 * - description (string | ReactNode, optional): Paragraph text below the heading.
 * - imageBg (string, optional): Tailwind bg class for the image column. Defaults to "".
 * - imageClassName (string, optional): Additional Tailwind classes for the image (e.g. object position). Defaults to "".
 * - items (array, optional): Array of bullet point objects with the following shape:
 *     - icon (string): URL of the icon image displayed in the orange rounded button.
 *     - plainIcon (boolean, optional): If true, renders the icon directly without the orange button wrapper.
 *     - heading (string): Bold heading for the bullet point.
 *     - text (string): Supporting description text for the bullet point.
 */

const ImageContentSection = ({
  imagePosition = "left",
  imageSrc,
  imageAlt,
  title,
  description,
  items,
  contentBg = "bg-blue/20",
  imageBg = "",
  imageClassName = "",
}) => {
  const isLeft = imagePosition === "left";

  return (
    <section className="relative flex flex-col md:block w-full my-16 md:my-24 lg:my-32 xl:my-52">

      {/* Desktop: absolutely positioned image fills the full section height */}
      <div className={`hidden md:block absolute top-0 bottom-0 w-1/2 overflow-hidden ${isLeft ? "left-0" : "right-0"} ${imageBg}`}>
        <img
          src={imageSrc}
          alt={imageAlt}
          className={`w-full h-full object-cover ${imageClassName}`}
          loading="lazy"
        />
      </div>

      {/* Mobile: normal flow image */}
      <div className={`md:hidden h-64 overflow-hidden ${imageBg}`}>
        <img
          src={imageSrc}
          alt={imageAlt}
          className={`w-full h-full object-cover ${imageClassName}`}
          loading="lazy"
        />
      </div>

      {/* Content — offset to the correct half on desktop */}
      <div className={`${contentBg} px-8 md:px-12 lg:px-16 xl:px-24 py-10 flex flex-col justify-center text-center md:text-left md:w-1/2 ${isLeft ? "md:ml-auto" : ""}`}>
        <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-7xl font-bold text-blue-dark1 mb-4 md:mb-6">
          {title}
        </h2>
        {description && (
          <p className="!text-sm md:!text-base xl:!text-xl leading-relaxed mb-8 md:mb-10">
            {description}
          </p>
        )}
        {items && (
          <div className="flex flex-col gap-6 xl:gap-10">
            {items.map((item, index) => (
              <div key={index} className="flex gap-4 xl:gap-6 justify-center md:justify-start">
                {item.plainIcon ? (
                  <img src={item.icon} alt="" className="w-6 h-6 xl:w-8 xl:h-8 min-w-[24px] shrink-0 mt-1" />
                ) : (
                  <div className="w-10 h-10 min-w-[40px] xl:w-14 xl:h-14 xl:min-w-[56px] rounded-[50%_50%_5px_50%] bg-orange flex items-center justify-center mt-1">
                    <img src={item.icon} alt="" className="w-5 h-5 xl:w-7 xl:h-7" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-blue-dark1 text-base md:text-lg xl:text-2xl mb-1">
                    {item.heading}
                  </h3>
                  <p className="!text-xs md:!text-sm xl:!text-lg leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ImageContentSection;
